import { DateTime } from 'luxon'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { BidType, ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU, MARKET_TIMEZONE } from '@/features/bidding/constants'
import { isPtuEditable } from '@/features/bidding/utils/isPtuEditable'
import type { AggregatedBid, AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import {
  getCountOfNonZeroPriceQuantities,
  getUniquePricesFromAllActivationGroups,
  isPortfolioEntelios,
} from '@/features/bidding/utils/validation/enteliosValidationUtils'

/**
 * Core validation flags returned by the validation logic
 */
export type AggregatedBidValidationFlags = {
  hasChunks: boolean
  overbiddingPtus: AggregatedBidPtu[]
  underbiddingPtus: AggregatedBidPtu[]
  enteliosViolationPtus: AggregatedBidPtu[]
}

/**
 * Core validation logic for aggregated bids.
 * This is the single source of truth for all validation rules.
 *
 * Returns validation flags that can be used by different consumers:
 * - UI: Convert to simple state ('valid' | 'invalid' | 'pending')
 * - Form validation: Build detailed error messages
 *
 * @param aggregatedBid - The aggregated bid to validate
 * @param activationGroup - The activation group context for Entelios-specific rules
 * @param bidsPerActivationGroup - Map of bids per activation group
 * @param bidType - The type of bid (i.e., CAPACITY, ENERGY)
 * @returns Validation flags indicating which rules passed/failed and which PTUs have issues
 */
export function validateAggregatedBidCore(
  aggregatedBid: AggregatedBid,
  activationGroup: ActivationGroup,
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  bidType: BidType,
): AggregatedBidValidationFlags {
  const hasChunks = aggregatedBid.ptus.every((ptu) => ptu.priceQuantities.length > 0)

  const overbiddingPtus = aggregatedBid.ptus.filter((ptu) => ptu.remaining < 0)

  // PM requires an expired PTU's volume to equal its baseline offeredQuantity while only the
  // answered chunks may be re-sent (see findExpiredPtuPrefillSource), so sum(chunks) < volume is the
  // valid submission there - PM itself enforces only sum(chunks) <= volume. An expired PTU is no
  // longer editable, so it can never be corrected on this page and must not block the day's submit.
  const currentTime = DateTime.now().setZone(MARKET_TIMEZONE)
  const underbiddingPtus = aggregatedBid.ptus.filter(
    (ptu) => ptu.remaining > 0 && isPtuEditable(currentTime, ptu.ptuStart, bidType, activationGroup.marketProgram),
  )

  const enteliosViolationPtus: AggregatedBidPtu[] = []
  if (isPortfolioEntelios(activationGroup.portfolio.code)) {
    for (const ptu of aggregatedBid.ptus) {
      if (
        getCountOfNonZeroPriceQuantities(ptu) > ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU ||
        (activationGroup.portfolio.countryCode.toUpperCase() === 'SE' &&
          bidType === BidType.CAPACITY &&
          getUniquePricesFromAllActivationGroups(ptu, bidsPerActivationGroup) > ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU)
      ) {
        // First check: allowed amount of prices within the same Activation Group for both CAPACITY and ENERGY bids
        // Second check: Additional SE Entelios rule for CAPACITY bids (only one price allowed per PTU for all Activation Groups)
        enteliosViolationPtus.push(ptu)
      }
    }
  }

  return {
    hasChunks,
    overbiddingPtus,
    underbiddingPtus,
    enteliosViolationPtus,
  }
}

/**
 * Validates an aggregated bid and returns a simple state for UI purposes.
 *
 * Used for:
 * - Tab icons (checkmark, error, or no icon)
 * - Real-time validation feedback during editing
 *
 * @param aggregatedBid - The aggregated bid to validate
 * @param activationGroup - The activation group context
 * @param bidsPerActivationGroup - Map of bids per activation group
 * @param bidType - The type of bid (i.e., CAPACITY, ENERGY)
 * @returns Simple validation state: 'valid' | 'invalid' | 'pending'
 */
export const validateAggregatedBid = (
  aggregatedBid: AggregatedBid,
  activationGroup: ActivationGroup,
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  bidType: BidType,
): 'valid' | 'invalid' | 'pending' => {
  const flags = validateAggregatedBidCore(aggregatedBid, activationGroup, bidsPerActivationGroup, bidType)

  if (!flags.hasChunks) return 'pending'

  const hasErrors =
    flags.overbiddingPtus.length > 0 || flags.underbiddingPtus.length > 0 || flags.enteliosViolationPtus.length > 0

  return hasErrors ? 'invalid' : 'valid'
}
